<script setup>
import { computed, watch } from 'vue'
import CloudinaryImage from '@components/CloudinaryImage.vue'
import CloudinaryVideo from '@components/CloudinaryVideo.vue'

const props = defineProps({
  publicId: {
    type: String,
    default: '',
  },
  cloudName: {
    type: String,
    default: () => import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'snapkit',
  },
  resourceType: {
    type: String,
    default: 'auto', // 'image', 'video', or 'auto' for detection
  },
  alt: {
    type: String,
    default: 'Cloudinary Media',
  },
})

// Video file extensions
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.ogv', '.m4v', '.mkv', '.flv']

// Detect if media is a video
const isVideo = computed(() => {
  // Prioritize explicit resource type from API or prop
  if (props.resourceType === 'video') {
    if (import.meta.env.DEV) {
      console.log('[CloudinaryMedia] Using explicit resourceType: video')
    }
    return true
  }

  if (props.resourceType === 'image') {
    if (import.meta.env.DEV) {
      console.log('[CloudinaryMedia] Using explicit resourceType: image')
    }
    return false
  }

  // Auto-detect based on file extension (only if publicId is available)
  if (props.publicId) {
    const publicIdLower = props.publicId.toLowerCase()
    const detected = VIDEO_EXTENSIONS.some((ext) => publicIdLower.endsWith(ext))

    if (import.meta.env.DEV) {
      console.log('[CloudinaryMedia] Auto-detected from extension:', {
        publicId: props.publicId,
        isVideo: detected,
      })
    }

    return detected
  }

  // Default to image if no publicId yet (loading state)
  return false
})

// Component to render
const componentToRender = computed(() => {
  return isVideo.value ? CloudinaryVideo : CloudinaryImage
})

// Watch for changes and log in dev mode
watch(
  () => [props.publicId, props.resourceType, isVideo.value],
  ([newId, newType, newIsVideo]) => {
    if (import.meta.env.DEV) {
      console.log('[CloudinaryMedia] Media type detection:', {
        publicId: newId || '(empty)',
        resourceType: newType,
        isVideo: newIsVideo,
        component: newIsVideo ? 'CloudinaryVideo' : 'CloudinaryImage',
      })
    }
  },
  { immediate: true }
)
</script>

<template>
  <component
    :is="componentToRender"
    :public-id="publicId"
    :cloud-name="cloudName"
    :alt="alt"
  />
</template>
